import { Component, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../../../shared/services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { LabelComponent } from '../../form/label/lable';
import { ButtonComponent } from '../../ui/button/button';
import { InputFieldComponent } from '../../form/input/input-field';
import { CommonModule } from '@angular/common';
import { passwordComplexityValidator, passwordComplexityMessage } from '../../../shared/validators/password.validator';

@Component({
  selector: 'app-forgot-password-form',
  imports: [
    LabelComponent,
    ButtonComponent,
    InputFieldComponent,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule
  ],
  templateUrl: './forgot-password-form.html'
})
export class ForgotPasswordForm {
  private fb = inject(FormBuilder);
  isLoading = false;
  token: string | null = null;
  showPassword = false;
  showConfirmPassword = false;

  constructor(private authService: AuthService, private toastr: ToastrService, private router: Router, private route: ActivatedRoute) { }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.token = params.get('token');
    });
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  form = this.fb.group({
    password: [
      '',
      [
        Validators.required,
        Validators.minLength(8),
        passwordComplexityValidator
      ]
    ],
    confirmPassword: ['', Validators.required]
  }, {
    validators: this.passwordMatchValidator
  });

  passwordMatchValidator(group: AbstractControl) {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

    if (password !== confirmPassword) {
      group.get('confirmPassword')?.setErrors({ mismatch: true });
    } else {
      group.get('confirmPassword')?.setErrors(null);
    }

    return null;
  }

  get f() {
    return this.form.controls;
  }

  fieldError(name: keyof typeof this.form.controls): string {
    const ctrl = this.f[name] as AbstractControl;

    if (!ctrl.invalid || !(ctrl.touched || ctrl.dirty)) return '';

    const e = ctrl.errors!;

    if (e['required'] || e['requiredTrue']) return this.requiredMsg(name);
    if (e['minlength']) return 'Minimum 8 characters required';
    if (e['needsUppercase'] || e['needsNumber'] || e['hasSpace']) return passwordComplexityMessage(e);
    if (e['mismatch']) return 'Passwords do not match';

    return 'Invalid field';
  }

  private requiredMsg(name: string): string {
    const map: Record<string, string> = {
      password: 'Password is required',
      confirmPassword: 'Confirm password is required'
    };
    return map[name] ?? 'This field is required';
  }

  onSubmit() {

    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsTouched();
      ctrl.markAsDirty();
    });

    if (this.form.invalid || !this.token) {
      this.toastr.error('Invalid or expired reset link');
      return;
    }

    this.isLoading = true;

    const { password } = this.form.getRawValue();

    this.authService.resetPassword({
      token: this.token,
      password: password!
    })
    .subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.toastr.success(res?.message || 'Password reset successfully');

        this.form.reset();

        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 1500);
      },
      error: (err: any) => {
        this.isLoading = false;
        this.toastr.error(
          err?.error?.message || 'Reset failed. Link may be expired.',
          'Error'
        );
      }
    });
  }
}
